import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppInfiniteQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { LiveShowsApi } from "./liveShows.api";

export namespace LiveShowsQueries {

export const moduleName = QueryModule.LiveShows;

export const keys = {
    all: [moduleName] as const,
    getPast: (limit?: number, order?: string, page?: number, cursor?: string, ) => [...keys.all, "/live-shows/past", limit, order, page, cursor] as const,
    getPastInfinite: (limit?: number, order?: string, cursor?: string, ) => [...keys.all, "/live-shows/past", "infinite", limit, order, cursor] as const,
    getUpcoming: (limit?: number, order?: string, page?: number, cursor?: string, ) => [...keys.all, "/live-shows/upcoming", limit, order, page, cursor] as const,
    getUpcomingInfinite: (limit?: number, order?: string, cursor?: string, ) => [...keys.all, "/live-shows/upcoming", "infinite", limit, order, cursor] as const,
    getCurrent: () => [...keys.all, "/live-shows/current", ] as const,
    getUploadedVideos: (limit?: number, order?: string, page?: number, cursor?: string, ) => [...keys.all, "/live-shows/uploaded-videos", limit, order, page, cursor] as const,
    getUploadedVideosInfinite: (limit?: number, order?: string, cursor?: string, ) => [...keys.all, "/live-shows/uploaded-videos", "infinite", limit, order, cursor] as const,
    findById: (id: string, ) => [...keys.all, "/live-shows/:id", id] as const,
};


 /** 
 * Query `useGetPast`
 * @summary Get past live shows
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<LiveShowsModels.LiveShowsGetPastResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetPast = <TData>({ limit, order, page, cursor }: { limit: number, order?: string, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof LiveShowsApi.getPast, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getPast(limit, order, page, cursor),
    queryFn: () =>  
    
    LiveShowsApi.getPast(limit, order, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetPastInfinite
 * @summary Get past live shows
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<LiveShowsModels.LiveShowsGetPastResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetPastInfinite = <TData>({ limit, order, cursor }: { limit: number, order?: string, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof LiveShowsApi.getPast, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getPastInfinite(limit, order, cursor),
    queryFn: ({ pageParam }) =>  
    
    LiveShowsApi.getPast(limit, order, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetUpcoming`
 * @summary Get upcoming live shows
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<LiveShowsModels.LiveShowsGetUpcomingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUpcoming = <TData>({ limit, order, page, cursor }: { limit: number, order?: string, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof LiveShowsApi.getUpcoming, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getUpcoming(limit, order, page, cursor),
    queryFn: () =>  
    
    LiveShowsApi.getUpcoming(limit, order, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetUpcomingInfinite
 * @summary Get upcoming live shows
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<LiveShowsModels.LiveShowsGetUpcomingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUpcomingInfinite = <TData>({ limit, order, cursor }: { limit: number, order?: string, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof LiveShowsApi.getUpcoming, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getUpcomingInfinite(limit, order, cursor),
    queryFn: ({ pageParam }) =>  
    
    LiveShowsApi.getUpcoming(limit, order, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetCurrent`
 * @summary Get currently live shows
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<LiveShowsModels.LiveShowsGetCurrentResponse> } List of currently live shows
 * @statusCodes [200, 401]
 */
export const useGetCurrent = <TData>(options?: AppQueryOptions<typeof LiveShowsApi.getCurrent, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getCurrent(),
    queryFn: LiveShowsApi.getCurrent
    ,
    ...options,
  });
};






 /** 
 * Query `useGetUploadedVideos`
 * @summary Get uploaded videos
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<LiveShowsModels.GetUploadedVideosResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUploadedVideos = <TData>({ limit, order, page, cursor }: { limit: number, order?: string, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof LiveShowsApi.getUploadedVideos, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getUploadedVideos(limit, order, page, cursor),
    queryFn: () =>  
    
    LiveShowsApi.getUploadedVideos(limit, order, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetUploadedVideosInfinite
 * @summary Get uploaded videos
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, title, provider, scheduledStartAt, scheduledEndAt, actualStartAt, actualEndAt, publishedAt, category, status, createdAt, updatedAt. Example: `id`
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<LiveShowsModels.GetUploadedVideosResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetUploadedVideosInfinite = <TData>({ limit, order, cursor }: { limit: number, order?: string, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof LiveShowsApi.getUploadedVideos, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getUploadedVideosInfinite(limit, order, cursor),
    queryFn: ({ pageParam }) =>  
    
    LiveShowsApi.getUploadedVideos(limit, order, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useFindById`
 * @summary Get a live show by ID
 * @param { string } object.id Path parameter. Live show ID
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.LiveShowResponseDto> } Live Show by ID
 * @statusCodes [200, 401]
 */
export const useFindById = <TData>({ id }: { id: string,  }, options?: AppQueryOptions<typeof LiveShowsApi.findById, TData>) => {
  
  
  return useQuery({
    queryKey: keys.findById(id),
    queryFn: () =>  
    
    LiveShowsApi.findById(id)
    ,
    ...options,
  });
};








 /** 
 * Mutation `useTrackWatched`
 * @summary Track watch event for a live show
 * @param { string } mutation.id Path parameter. Live show ID
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } Watch event tracked successfully
 * @statusCodes [204, 401, 404]
 */
export const useTrackWatched = (options?: AppMutationOptions<typeof LiveShowsApi.trackWatched, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            LiveShowsApi.trackWatched(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
